import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Upload, Table, Tag, Alert, Space } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import { API_TXALERT } from "../../config/config";
import ToBase64 from "../../helper/ToBase64";
import formatFileSize from "../../helper/FormatFileSize";
import { normalizeFilename, showDuplicateError, isDuplicateResult, duplicateMessage, resultMessage } from "../../helper/CreditDocumentDuplicate";
import UPLOAD_SUCCESS_DURATION_MS from "../../helper/UploadSuccessDuration";
import { parseAlertId } from "../../helper/CreditDocumentAlertId";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg"];
// Base64 inflates payload by ~33%, so 5MB raw ≈ 6.7MB base64 - stays under the server's 8MB post_max_size limit.
const MAX_TOTAL_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_SIZE_MB = MAX_TOTAL_SIZE_BYTES / (1024 * 1024);

const getFileExtension = (filename) => {
    const idx = filename.lastIndexOf(".");
    return idx !== -1 ? filename.substring(idx).toLowerCase() : "";
};

const getMimeType = (file) => file.type || "application/octet-stream";

const UNCOUNTED_ERRORS = new Set(["file_too_large", "batch_too_large", "unsupported_type", "invalid_format"]);

const validateFile = (file) => {
    const ext = getFileExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        return "unsupported_type";
    }
    if (file.size > MAX_TOTAL_SIZE_BYTES) {
        return "file_too_large";
    }
    const alertId = parseAlertId(file.name);
    if (!alertId) {
        return "invalid_format";
    }
    return null;
};

const BatchCreditDocumentUpload = ({ t, onUploadSuccess }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const checkTimerRef = useRef(null);
    const totalSizeRef = useRef(0);
    // Mirrors the selected filenames so a multi-file drop sees files added earlier in the same drop.
    const namesRef = useRef(new Set());

    const showModal = () => setIsModalVisible(true);

    const handleCancel = () => {
        setFiles([]);
        totalSizeRef.current = 0;
        namesRef.current = new Set();
        setLoading(false);
        setChecking(false);
        setIsModalVisible(false);
    };

    const handleRemoveFile = (uid) => {
        setFiles((prev) => {
            const entry = prev.find((f) => f.uid === uid);
            if (entry && !UNCOUNTED_ERRORS.has(entry.error)) {
                totalSizeRef.current = Math.max(0, totalSizeRef.current - entry.file.size);
            }
            const next = prev.filter((f) => f.uid !== uid);
            namesRef.current = new Set(next.map((f) => normalizeFilename(f.file.name)));
            return next;
        });
    };

    const handleRemoveInvalidFiles = () => {
        setFiles((prev) => {
            const next = prev.filter((f) => !f.error);
            totalSizeRef.current = next.reduce((sum, f) => sum + f.file.size, 0);
            namesRef.current = new Set(next.map((f) => normalizeFilename(f.file.name)));
            return next;
        });
    };

    const validFiles = files.filter((f) => !f.error && f.checked);
    const invalidFiles = files.filter((f) => f.error);
    // Same files totalSizeRef counts against the limit, so the total matches the batch_too_large check.
    const totalSizeBytes = files
        .filter((f) => !UNCOUNTED_ERRORS.has(f.error))
        .reduce((sum, f) => sum + (f.file.size || 0), 0);

    useEffect(() => {
        const pendingFiles = files.filter((f) => !f.error && !f.checked);
        if (pendingFiles.length === 0) {
            setChecking(false);
            return;
        }

        if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
        setChecking(true);
        let cancelled = false;

        checkTimerRef.current = setTimeout(async () => {
            const alertIds = [...new Set(pendingFiles.map((f) => Number(f.alertId)).filter(Boolean))];
            try {
                const result = await axios.post(
                    API_TXALERT + "/credit/check",
                    { alertids: alertIds },
                    { headers: { Authorization: "Bearer " + Cookies.get("access_token") } }
                );
                if (cancelled) return;
                const validIds = result.data?.alertids || [];
                setFiles((prev) =>
                    prev.map((f) => {
                        if (f.checked || f.error) return f;
                        const hasCredit = validIds.includes(Number(f.alertId));
                        return { ...f, checked: true, error: hasCredit ? null : "no_credit_request" };
                    })
                );
            } catch {
                if (cancelled) return;
                setFiles((prev) =>
                    prev.map((f) => (f.checked || f.error ? f : { ...f, checked: true, error: "check_failed" }))
                );
            }
            if (!cancelled) setChecking(false);
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(checkTimerRef.current);
        };
    }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpload = async () => {
        if (validFiles.length === 0) return;
        setLoading(true);

        try {
            const documents = await Promise.all(
                validFiles.map(async (f) => {
                    const base64Full = await ToBase64(f.file);
                    const content = base64Full.replace(/^data:[^;]+;base64,/, "");
                    return {
                        filename: f.file.name,
                        filetype: getMimeType(f.file),
                        content: content,
                    };
                })
            );

            const response = await axios.post(
                API_TXALERT + "/credit/documents/batchupload",
                { documents: documents },
                { headers: { Authorization: "Bearer " + Cookies.get("access_token") } }
            );

            const result = response.data;
            const successCount = result.uploaded || 0;
            const failedResults = (result.results || []).filter((r) => !r.status);
            const failCount = failedResults.length;
            const errors = failedResults.map((r) => {
                const alertId = parseAlertId(r.filename || "");
                return `Alert #${alertId || "?"} (${r.filename}): ${resultMessage(t, r)}`;
            });
            const succeededFilenames = (result.results || []).filter((r) => r.status).map((r) => r.filename);
            const failedByName = new Map(failedResults.map((r) => [r.filename, isDuplicateResult(r) ? "duplicate" : r.message]));

            setLoading(false);
            // Rejected rows keep the server's reason in their Status column.
            if (failCount > 0) {
                setFiles((prev) =>
                    prev.map((f) => (failedByName.has(f.file.name) ? { ...f, error: failedByName.get(f.file.name) } : f))
                );
            }

            if (successCount > 0 && failCount === 0) {
                handleCancel();
                Swal.fire({
                    icon: "success",
                    title: t("contents.credit_report.batch_upload_success") || "Documents uploaded successfully",
                    text: `${successCount} file(s) uploaded`,
                    showConfirmButton: false,
                    timer: UPLOAD_SUCCESS_DURATION_MS,
                    timerProgressBar: true,
                });
                if (onUploadSuccess) onUploadSuccess();
            } else if (successCount > 0 && failCount > 0) {
                setFiles((prev) => {
                    const next = prev.filter((f) => !succeededFilenames.includes(f.file.name));
                    totalSizeRef.current = next
                        .filter((f) => !UNCOUNTED_ERRORS.has(f.error))
                        .reduce((sum, f) => sum + f.file.size, 0);
                    namesRef.current = new Set(next.map((f) => normalizeFilename(f.file.name)));
                    return next;
                });
                Swal.fire({
                    icon: "warning",
                    title: t("contents.credit_report.batch_upload_partial") || "Partial upload",
                    html: `${successCount} file(s) uploaded, ${failCount} failed.<br/><br/>${errors.map(e => e.replace(/</g, '&lt;').replace(/>/g, '&gt;')).join("<br/>")}`,
                });
                if (onUploadSuccess) onUploadSuccess();
            } else {
                Swal.fire({
                    icon: "error",
                    title: t("contents.credit_report.batch_upload_error") || "Upload failed",
                    html: errors.length > 0
                        ? errors.map(e => e.replace(/</g, '&lt;').replace(/>/g, '&gt;')).join("<br/>")
                        : (result?.message || "Upload failed"),
                });
            }
        } catch (error) {
            setLoading(false);
            const errData = error?.response?.data;
            const msg = errData?.error_msg?.message || errData?.message || errData?.msg || error.message || "Upload failed";
            Swal.fire({
                icon: "error",
                title: t("contents.credit_report.batch_upload_error") || "Upload failed",
                text: msg,
            });
        }
    };

    const uploadProps = {
        accept: ACCEPTED_EXTENSIONS.join(", "),
        multiple: true,
        showUploadList: false,
        beforeUpload: (file) => {
            const name = normalizeFilename(file.name);
            if (namesRef.current.has(name)) {
                showDuplicateError(t, file.name);
                return false;
            }
            namesRef.current.add(name);
            const validationError = validateFile(file);
            let error = validationError;
            if (!error) {
                if (totalSizeRef.current + file.size > MAX_TOTAL_SIZE_BYTES) {
                    error = "batch_too_large";
                } else {
                    totalSizeRef.current += file.size;
                }
            }
            const alertId = parseAlertId(file.name);
            setFiles((prev) => [...prev, { uid: file.uid, file, alertId, error, checked: !!error }]);
            return false;
        },
    };

    const getErrorMessage = (error) => {
        switch (error) {
            case "invalid_format":
                return t("contents.credit_report.batch_upload_invalid_format") || "Invalid filename format";
            case "file_too_large":
                return t("contents.credit_report.batch_upload_file_too_large") || `File too large (max ${MAX_TOTAL_SIZE_MB}MB)`;
            case "batch_too_large":
                return t("contents.credit_report.batch_upload_batch_too_large") || `Batch size limit exceeded (max ${MAX_TOTAL_SIZE_MB}MB total)`;
            case "unsupported_type":
                return t("contents.credit_report.batch_upload_unsupported_type") || "Unsupported file type";
            case "no_credit_request":
                return t("contents.credit_report.batch_upload_no_credit_request") || "No credit request found";
            case "check_failed":
                return t("contents.credit_report.batch_upload_check_failed") || "Verification failed";
            case "duplicate":
                return duplicateMessage(t);
            default:
                return error;
        }
    };

    const tableColumns = [
        {
            title: t("tables.fields.filename") || "File Name",
            dataIndex: "file",
            key: "filename",
            render: (file) => (
                <>
                    {file.name}{" "}
                    <span style={{ color: "#999", fontSize: 12, whiteSpace: "nowrap" }}>({formatFileSize(file.size)})</span>
                </>
            ),
        },
        {
            title: t("tables.fields.alertid") || "Alert ID",
            dataIndex: "alertId",
            key: "alertId",
            render: (alertId) => alertId || "-",
        },
        {
            title: t("tables.fields.status") || "Status",
            dataIndex: "error",
            key: "status",
            render: (error, record) =>
                !record.checked ? (
                    <Tag color="processing">{t("contents.credit_report.batch_upload_checking") || "Checking..."}</Tag>
                ) : error ? (
                    <Tag color="error">{getErrorMessage(error)}</Tag>
                ) : (
                    <Tag color="success">{t("contents.credit_report.batch_upload_valid") || "Valid"}</Tag>
                ),
        },
        {
            title: t("tables.fields.action") || "Action",
            key: "action",
            width: 80,
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveFile(record.uid)}
                    size="small"
                    style={{ padding: 0 }}
                >
                    {t("tables.actions.remove") || "Remove"}
                </Button>
            ),
        },
    ];

    return (
        <>
            <Button icon={<UploadOutlined />} onClick={showModal}>
                {t("contents.credit_report.batch_upload") || "Batch Upload Documents"}
            </Button>
            <Modal
                title={t("contents.credit_report.batch_upload_title") || "Batch Upload Credit Documents"}
                width={700}
                visible={isModalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        {t("contents.chargebacks.form.cancel") || "Cancel"}
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        disabled={validFiles.length === 0 || invalidFiles.length > 0 || loading || checking}
                        onClick={handleUpload}
                    >
                        {loading
                            ? t("contents.chargebacks.form.uploading") || "Uploading..."
                            : `${t("contents.chargebacks.form.upload") || "Upload"} (${validFiles.length})`}
                    </Button>,
                ]}
            >
                <Alert
                    message={t("contents.credit_report.batch_upload_naming") || "File naming pattern: {alertid}_{filename}.{ext}"}
                    description={`${t("contents.credit_report.batch_upload_example") || "Example"}: 12345_chargebackdocument.pdf`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Upload.Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        {t("contents.credit_report.batch_upload_drag") || "Click or drag files to this area to upload"}
                    </p>
                    <p className="ant-upload-hint">
                        {t("contents.credit_report.batch_upload_hint") || `Supported file types: PDF, DOC, DOCX, PNG, JPG, JPEG. Maximum combined file size: ${MAX_TOTAL_SIZE_MB} MB.`}
                    </p>
                </Upload.Dragger>
                {files.length > 0 && (
                    <>
                        <div style={{ margin: "16px 0 8px" }}>
                            <Space>
                                <span>
                                    {t("contents.credit_report.batch_upload_valid") || "Valid"}: <strong>{validFiles.length}</strong>
                                </span>
                                {invalidFiles.length > 0 && (
                                    <span style={{ color: "#f5222d" }}>
                                        {t("contents.credit_report.batch_upload_invalid") || "Invalid"}: <strong>{invalidFiles.length}</strong>
                                    </span>
                                )}
                            </Space>
                        </div>
                        {invalidFiles.length > 0 && (
                            <Alert
                                message={`${invalidFiles.length} ${t("contents.credit_report.batch_upload_invalid_found") || "invalid file(s) found. Please remove them before uploading."}`}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 8 }}
                                action={
                                    <Button size="small" danger disabled={loading} onClick={handleRemoveInvalidFiles}>
                                        {t("contents.credit_report.batch_upload_remove_invalid") || "Remove invalid files"}
                                    </Button>
                                }
                            />
                        )}
                        <Table
                            size="small"
                            rowKey="uid"
                            columns={tableColumns}
                            dataSource={files}
                            pagination={false}
                            scroll={{ y: 300 }}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 8px 0 0", borderTop: "1px solid #f0f0f0", fontSize: 12 }}>
                            <span style={{ color: totalSizeBytes > MAX_TOTAL_SIZE_BYTES ? "#ff4d4f" : "#52c41a", fontWeight: 500 }}>
                                {t("contents.alerts.total_size") || "Total:"} {formatFileSize(totalSizeBytes)} / {MAX_TOTAL_SIZE_MB}MB
                            </span>
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

export default BatchCreditDocumentUpload;
